import { isPlcSystemType } from '../config/systemTypes';

/**
 * What the delete dialog has to say — and do — about an IoT connection.
 *
 * The two node types differ in more than wording: an asset's connection is
 * detached by us via `deactivateIoTConnection` before the delete, while a
 * system's connection has no detach entry point in the schema at all
 * (`deactivateIoTConnection` is assetLocation-scoped). For a system the
 * backend detaches as part of `deleteInstalledBaseNode`, so `'system'` only
 * ever drives the warning copy.
 */
export type DeleteIotDetach =
    | { kind: 'asset'; assetLocationId: string; iotConnectionId: string }
    | { kind: 'system' }
    | null;

/** The subset of an InstalledBaseNode this decision needs. */
export interface DeleteIotDetachNode {
    nodeType?: { key?: string | null } | null;
    assetLocation?: {
        id?: string | null;
        iotConnection?: { id?: string | null } | null;
    } | null;
    system?: {
        systemType?: string | null;
        iotConnection?: { id?: string | null } | null;
    } | null;
}

export const resolveDeleteIotDetach = (
    node: DeleteIotDetachNode | null | undefined,
): DeleteIotDetach => {
    const key = node?.nodeType?.key;

    if (key === 'ASSET') {
        const assetLocationId = node?.assetLocation?.id;
        const iotConnectionId = node?.assetLocation?.iotConnection?.id;
        return assetLocationId && iotConnectionId
            ? { kind: 'asset', assetLocationId, iotConnectionId }
            : null;
    }

    if (key === 'SYSTEM') {
        // A PLC system type alone is not enough: a system can carry type 2/3/4
        // while no device has been registered yet (the state that renders the
        // registration iframe instead of telemetry). Warning there would
        // announce a detach that cannot happen.
        const hasConnection = !!node?.system?.iotConnection?.id;
        return isPlcSystemType(node?.system?.systemType) && hasConnection
            ? { kind: 'system' }
            : null;
    }

    return null;
};
